import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import { IconChevronDown, IconCircleCheckFilled } from '@tabler/icons-react';
import EmailEditor from 'react-email-editor';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import appSettings from '../../app-settings';
import Input from '../inputs/Input';
import Button from '../buttons/Button';
import { EmailWithDomainInput } from 'ui';
import Loader from '../loader/Loader';
import AlertMessage from '../alerts/AlertMessage';
import { useAccount } from '../../store/account/hooks';
import { useDomains } from '../../store/domains/hooks';
import { usePages } from '../../store/pages/hooks';
import { getEditorOptions, getLinkTypes, getNetworkError, injectTemplateDesignSections } from '../../core/utils';
import { getPageUrl } from '../../core/utils/pages';
import { registerGalleryFileManager } from '../../core/utils/unlayer-file-manager';
import { getDefaultSectionTemplates } from '../../api';

const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;

const bodyValues = {
  fontFamily: {
    label: 'Lato',
    value: "'Lato', Tahoma, Verdana, sans-serif",
  },
};

const createTemplate = async (design, { order, type }, emailConfig) => {
  const res = await axios.post(EMAIL_TEMPLATES_URL, { design: JSON.stringify(design), order, type, emailConfig });
  return res.data;
};

const uploadHtml = async (uploadUrl, html) => {
  await axios.put(uploadUrl, html, { headers: { 'Content-Type': 'text/html', Authorization: '' } });
};

const updateTemplate = async (templateId, version, design, emailConfig) => {
  const res = await axios.put(`${EMAIL_TEMPLATES_URL}/${templateId}/${version}`, {
    design: JSON.stringify(design),
    emailConfig,
  });
  return res.data;
};

const WelcomeEmailAccordion = ({ template, isOpen, onToggle, onSaveSuccess }) => {
  const editorRef = useRef(null);
  const [editorLoading, setEditorLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [localTemplate, setLocalTemplate] = useState(template);

  const { account } = useAccount();
  const { pageList } = usePages();
  const { domain } = useDomains();

  const [emailConfig, setEmailConfig] = useState({
    subject: template.emailConfig?.subject ?? '',
    senderName: template.emailConfig?.senderName ?? '',
    senderEmail: template.emailConfig?.senderEmail?.split('@')[0] ?? '',
    previewText: template.emailConfig?.previewText ?? '',
  });

  const isComplete =
    !!template.templateId &&
    !!template.emailConfig?.subject?.trim() &&
    !!template.emailConfig?.senderName?.trim() &&
    !!template.emailConfig?.senderEmail?.trim() &&
    !!template.emailConfig?.previewText?.trim();

  const editorOptions = useMemo(() => getEditorOptions({ account, campaignDetails: {} }), [account]);

  // Reset email config when accordion opens
  useEffect(() => {
    if (isOpen) {
      setEmailConfig({
        subject: template.emailConfig?.subject ?? '',
        senderName: template.emailConfig?.senderName ?? '',
        senderEmail: template.emailConfig?.senderEmail?.split('@')[0] ?? '',
        previewText: template.emailConfig?.previewText ?? '',
      });
      setEditorLoading(true);
      setLocalTemplate(template);
    }
  }, [isOpen, template]);

  // Load default section templates if no design exists
  useEffect(() => {
    const loadDefaultTemplate = async () => {
      if (!isOpen || localTemplate?.design || localTemplate?.templateId || !account?.emailPreferences?.sections) {
        setLoadingTemplate(false);
        return;
      }

      try {
        const sectionTemplates = await getDefaultSectionTemplates(account.emailPreferences.sections);
        const design = JSON.stringify(await import(`../email-edits/templates/${localTemplate?.source}`));
        setLocalTemplate({ ...localTemplate, design: injectTemplateDesignSections(design, sectionTemplates) });
      } catch (err) {
        Sentry.captureException(err);
      }
      setLoadingTemplate(false);
    };

    loadDefaultTemplate();
  }, [isOpen, localTemplate, account?.emailPreferences?.sections]);

  const exportHtml = useCallback(async () => {
    return new Promise((resolve, reject) => {
      if (!editorRef.current?.editor) reject(new Error('Editor not ready'));
      else editorRef.current.editor.exportHtml((data) => resolve(data.html));
    });
  }, []);

  const exportDesign = useCallback(async () => {
    return new Promise((resolve, reject) => {
      if (!editorRef.current?.editor) reject(new Error('Editor not ready'));
      else editorRef.current.editor.saveDesign((design) => resolve(design));
    });
  }, []);

  const handleEditorReady = useCallback(
    (unlayer) => {
      registerGalleryFileManager(unlayer);

      if (localTemplate?.design) {
        try {
          unlayer.loadDesign(JSON.parse(localTemplate.design));
        } catch (err) {
          Sentry.captureException(err);
        }
      }

      unlayer.setBodyValues(bodyValues);

      const donationPageUrl = getPageUrl({ account, params: { campaignId: '' } });
      unlayer.setLinkTypes(getLinkTypes({ donationPageUrl, pageList, account }));

      setEditorLoading(false);
    },
    [localTemplate?.design, account, pageList],
  );

  const handleSave = async () => {
    setSaving(true);
    setError(false);

    try {
      const html = await exportHtml();
      const design = await exportDesign();

      const fullEmailConfig = {
        subject: emailConfig.subject,
        senderName: emailConfig.senderName,
        senderEmail: emailConfig.senderEmail ? `${emailConfig.senderEmail}@${domain?.domain}` : '',
        previewText: emailConfig.previewText,
        returnEmail: '',
      };

      const { uploadUrl } =
        template.templateId && template.templateVersion
          ? await updateTemplate(template.templateId, template.templateVersion, design, fullEmailConfig)
          : await createTemplate(design, { order: template.order, type: template.type }, fullEmailConfig);

      if (uploadUrl) {
        await uploadHtml(uploadUrl, html);
      }

      onSaveSuccess?.();
    } catch (err) {
      Sentry.captureException(err);
      setError(getNetworkError(err));
    }

    setSaving(false);
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfig((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div>
      <div
        className="flex w-full cursor-pointer items-center justify-between p-8"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex items-center gap-4">
          <h3 className="text-h4 font-semibold">{template.title}</h3>
        </div>
        {isOpen ? (
          <div className="flex items-center gap-4">
            <Button
              title="Cancel"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              disabled={saving}
            />
            <Button
              title="Save Email"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              loading={saving}
              disabled={saving}
            />
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <IconCircleCheckFilled size={28} className={isComplete ? 'text-green-500' : 'text-gray-200'} />
            <IconChevronDown
              size={20}
              className={`text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        )}
      </div>

      <Collapse in={isOpen} unmountOnExit>
        <div className="border-t border-gray-100 p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Input
                id={`subject-${template.order}`}
                label="Subject Line"
                value={emailConfig.subject}
                onChange={(e) => handleEmailConfigChange('subject', e.target.value)}
                placeholder="Enter email subject"
                size="lg"
              />
              <Input
                id={`senderName-${template.order}`}
                label="Sender Name"
                value={emailConfig.senderName}
                onChange={(e) => handleEmailConfigChange('senderName', e.target.value)}
                placeholder="Enter sender name"
                size="lg"
              />
              <Input
                id={`previewText-${template.order}`}
                label="Preview Text"
                value={emailConfig.previewText}
                onChange={(e) => handleEmailConfigChange('previewText', e.target.value)}
                placeholder="Add preview text"
                size="lg"
              />
              <EmailWithDomainInput
                id={`senderEmail-${template.order}`}
                label="Sender Email"
                value={emailConfig.senderEmail}
                onChange={(e) => handleEmailConfigChange('senderEmail', e.target.value)}
                authenticatedDomain={domain?.domain}
                placeholder="Enter sender email"
                size="lg"
              />
            </div>

            {/* Email Editor */}
            <div className="border border-gray-200">
              {(editorLoading || loadingTemplate) && (
                <div className="flex h-[550px] items-center justify-center">
                  <Loader />
                </div>
              )}
              {!loadingTemplate && (
                <div className={editorLoading ? 'invisible h-0' : ''}>
                  <EmailEditor ref={editorRef} onReady={handleEditorReady} options={editorOptions} minHeight={550} />
                </div>
              )}
            </div>
          </div>

          <AlertMessage
            open={!!error}
            message={typeof error === 'string' ? error : 'Failed to save email. Please try again.'}
            onClose={() => setError(false)}
            severity="error"
          />
        </div>
      </Collapse>
    </div>
  );
};

WelcomeEmailAccordion.propTypes = {
  template: PropTypes.shape({
    title: PropTypes.string.isRequired,
    image: PropTypes.string,
    source: PropTypes.string,
    type: PropTypes.string,
    order: PropTypes.number.isRequired,
    design: PropTypes.string,
    downloadUrl: PropTypes.string,
    templateId: PropTypes.string,
    templateVersion: PropTypes.string,
    emailConfig: PropTypes.shape({
      subject: PropTypes.string,
      senderName: PropTypes.string,
      senderEmail: PropTypes.string,
      previewText: PropTypes.string,
    }),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func,
};

export default WelcomeEmailAccordion;
